import Permissions from '@app/src/auth/permissions'
import CategoryCell from '@app/src/components/Table/Cells/CategoryCell'
import CountryCell from '@app/src/components/Table/Cells/CountryCell'
import DateCell from '@app/src/components/Table/Cells/DateCell'
import EmptyCell from '@app/src/components/Table/Cells/EmptyCell'
import EnrichedValueCell from '@app/src/components/Table/Cells/EnrichedValueCell'
import EnumCategoryCell from '@app/src/components/Table/Cells/EnumCategoryCell'
import FinalRiskRatingCell from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import ProviderConnectedCell from '@app/src/components/Table/Cells/ProviderConnectedCell'
import ProviderSpendCell from '@app/src/components/Table/Cells/ProviderSpendCell'
import ReferralContactCell from '@app/src/components/Table/Cells/ReferralContactCell'
import TagsCell from '@app/src/components/Table/Cells/TagsCell'
import UserGroupCell from '@app/src/components/Table/Cells/UserGroupCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import { useProviderEnrichedValues } from '@app/src/hooks/useProviderEnrichedValues'
import {
  categoryOptionsToFilterGroups,
  dateRangeToFilterGroups,
  enumMultiSelectFilter as sharedEnumMultiSelectFilter,
  enumToFilterGroups,
  facetMultiSelectFilter,
  formatEnumFacetLabel,
  multiSelectToIn,
} from '@app/src/pages/ResourceCollection/MrtCollection/filterBuilders'
import {
  MrtColumnGroup,
  MrtColumnSubgroup,
  type MrtColumnConfig,
  type MrtColumnFilter,
  type MrtCountEndpoint,
} from '@app/src/pages/ResourceCollection/MrtCollection/mrtCollectionTypes'
import { countTargetOf } from '@app/src/pages/ResourceCollection/MrtCollection/providerFilterColumn'
import {
  spendColumnFilter,
  spendHeader,
} from '@app/src/pages/ResourceCollection/MrtCollection/spendFilter/spendColumnFilter'
import {
  createSpendToFilterGroups,
  spendDisplayPeriod,
  spendFilterFields,
} from '@app/src/pages/ResourceCollection/MrtCollection/spendFilter/spendFilterValue'
import type { SpendFilterOptions } from '@app/src/pages/ResourceCollection/MrtCollection/spendFilter/useSpendFilterOptions'
import { NO_SPEND_OPTIONS } from '@app/src/pages/ResourceCollection/MrtCollection/spendFilter/useSpendFilterOptions'
import type { Category, CategoryWithId } from '@app/src/types/categories'
import {
  FinalRiskRating,
  ProviderActivityStatus,
  ProviderApprovalStatus,
  ProviderPriority,
  SupplierUsage,
} from '@app/src/types/categories'
import type { Provider } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import type { Unit } from '@app/src/types/resourceExplorer'
import { isEmptyEquivalentValue } from '@app/src/utils/emptyEquivalentValues'
import { insertIf } from '@app/src/utils/helpersTs'
import React from 'react'
import type { IntlShape } from 'react-intl'
import { asFilterOnlyColumn } from '../asFilterOnlyColumn'
import { withProviderPath } from '../withProviderPath'

type EnrichedStringField = 'websiteAddress' | 'registrationNumber' | 'vatNumber' | 'revenueRange' | 'companySizeRange'

const EnrichedProviderValueCell: React.FC<{
  provider: Provider
  field: EnrichedStringField
  isLink?: boolean
  formatValue?: (value: string) => string
}> = ({ provider, field, isLink, formatValue }) => {
  const enrichedValues = useProviderEnrichedValues(provider)
  const { value, isEnriched } = enrichedValues[field]
  if (!value) return <EmptyCell disableCell />
  const displayValue = formatValue ? formatValue(value) : value
  return <EnrichedValueCell value={displayValue} isEnriched={isEnriched} isLink={isLink} disableCell />
}

const EnrichedProviderIndustryCell: React.FC<{ provider: Provider }> = ({ provider }) => {
  const { industryId } = useProviderEnrichedValues(provider)
  if (!provider.industry?.description) return <EmptyCell disableCell />
  return <EnrichedValueCell value={provider.industry.description} isEnriched={industryId.isEnriched} disableCell />
}

const EnrichedProviderSubIndustryCell: React.FC<{ provider: Provider }> = ({ provider }) => {
  const { subIndustryId } = useProviderEnrichedValues(provider)
  if (!provider.subIndustry?.description) return <EmptyCell disableCell />
  return (
    <EnrichedValueCell value={provider.subIndustry.description} isEnriched={subIndustryId.isEnriched} disableCell />
  )
}

// Tier is a number rather than an enum, so it can't go through formatEnumFacetLabel: tier 0 is the
// value that means no tier has been picked, and gets the same "Empty" label its cells show.
export const formatTierLabel =
  (formatMessage: IntlShape['formatMessage']) =>
  (raw: string): string =>
    Number(raw) === 0
      ? formatMessage({ id: 'general.empty' })
      : formatMessage({ id: 'schemas.provider.tierValues' }, { tier: raw })

export type ProviderGroupColumnsArgs = {
  // The path from the collection's own entity to the provider ('product.provider'), or '' when the row
  // is the provider itself. Under a path the group is filter-only: only a row that is the provider shows its cells.
  providerPath: string
  // The table's own count endpoint, for the values its facets can't count. Without one, under a path, they
  // are counted on all the providers.
  countEndpoint?: MrtCountEndpoint
  organizationCurrency?: Unit
  spendPeriodFocus?: string
  spendFilterOptions?: SpendFilterOptions
  getLocalisedCountryName?: (countryCode?: string) => string
  formatMessage: IntlShape['formatMessage']
  hasPermission?: (permission: Permissions) => boolean
  categories?: Category[]
}

// Under a path these are the ids the provider filters had there before this group, so the chips and views
// stored with them still apply, and they don't take the place of the page's own columns of the same name.
const ID_UNDER_PATH: Record<string, string> = {
  ownerUsers: 'providerOwners',
  providerApprovalStatus: 'providerApprovalStatus',
  supplierUsage: 'supplierUsage',
}

const prefixProviderId = (id: string): string =>
  ID_UNDER_PATH[id] ?? `provider${id.charAt(0).toUpperCase()}${id.slice(1)}`

/**
 * The provider properties as columns, for any table whose rows are or reach a provider. A table gets
 * them by declaring providerGroup in its config's `groups`.
 */
export const getProviderGroupColumns = <T,>({
  providerPath,
  countEndpoint,
  organizationCurrency,
  spendPeriodFocus,
  spendFilterOptions = NO_SPEND_OPTIONS,
  getLocalisedCountryName,
  formatMessage,
  hasPermission,
  categories,
}: ProviderGroupColumnsArgs): MrtColumnConfig<T>[] => {
  const canSeeNonFinanceColumns = !hasPermission || !hasPermission(Permissions.FINANCE_USER)
  const isSourcingUser = hasPermission?.(Permissions.SOURCING_USER) ?? false
  const providerField = (path: string): string => withProviderPath(providerPath, path)
  const spendCountTarget = countTargetOf({ providerPath, countEndpoint })

  // Cells only render without a path, where the row is the provider.
  const renderProviderCell =
    (render: (provider: Provider) => React.ReactNode) =>
    (row: T): React.ReactNode =>
      render(row as Provider)

  const renderSpendCell = (periodName?: string) =>
    renderProviderCell(provider => {
      const spend = provider.spends?.find(spend => spend.periodName === periodName)
      if (!spend?.amount && spend?.amount !== 0) return <EmptyCell disableCell />
      return <ProviderSpendCell spend={spend} organizationCurrency={organizationCurrency} disableCell />
    })

  const multiSelectFilter = (path: string, facetName: string, asNumber: boolean, isEnum?: boolean): MrtColumnFilter =>
    facetMultiSelectFilter(providerField(path), providerField(facetName), asNumber, isEnum)

  const enumMultiSelectFilter = (
    field: string,
    valuesIntlPrefix: string,
    enumWithNotSelected?: Record<string, string>,
  ): MrtColumnFilter =>
    sharedEnumMultiSelectFilter(providerField(field), valuesIntlPrefix, formatMessage, enumWithNotSelected)

  const categoryColumns = (categories ?? [])
    .filter((category): category is CategoryWithId => category.id !== undefined)
    .map<MrtColumnConfig<T>>(category => ({
      id: `category-${category.id}`,
      subgroup: MrtColumnSubgroup.CustomCategories,
      header: category.name,
      includes: [providerField('categoryOptions.category')],
      renderCell: renderProviderCell(provider => {
        const tags = (provider.categoryOptions ?? [])
          .filter(option => option.categoryId === category.id)
          .map(option => option.name)
        if (!tags.length) return <EmptyCell disableCell />
        return <TagsCell tags={tags} amountOfTagsShow={2} singleLine disableCell />
      }),
      // Options come from this category's own options (fetched on demand), not a facet.
      filter: {
        field: providerField('categoryOptions.id'),
        variant: 'multi-select',
        categoryId: category.id,
        toFilterGroups: categoryOptionsToFilterGroups(providerField('categoryOptions'), category.id),
      },
    }))

  const columns: MrtColumnConfig<T>[] = [
    {
      id: 'linkStatus',
      subgroup: MrtColumnSubgroup.StatusAndAdministration,
      headerIntlId: 'schemas.supplier.connectionStatus',
      includes: [providerField('referralContact')],
      enableSorting: true,
      sortField: providerField('linkStatus'),
      renderCell: renderProviderCell(provider => <ProviderConnectedCell provider={provider} disableCell />),
      filter: {
        field: providerField('linkStatus'),
        variant: 'multi-select',
        facet: {
          name: providerField('linkStatus'),
          isEnum: true,
          matchInSearch: true,
          formatLabel: formatEnumFacetLabel('resourceExplorer.provider.status', formatMessage),
        },
        toFilterGroups: multiSelectToIn(providerField('linkStatus'), false),
      },
    },
    {
      id: 'country',
      subgroup: MrtColumnSubgroup.CompanyDetails,
      headerIntlId: 'schemas.supplier.country',
      includes: [providerField('country')],
      enableSorting: true,
      sortField: providerField('country.name'),
      renderCell: renderProviderCell(provider =>
        provider.country?.countryCode ? (
          <CountryCell countryCode={provider.country.countryCode} disableCell />
        ) : (
          <EmptyCell disableCell />
        ),
      ),
      filter: {
        field: providerField('country.id'),
        variant: 'multi-select',
        facet: { name: providerField('country.iso3166Alpha2'), formatLabel: getLocalisedCountryName },
        toFilterGroups: multiSelectToIn(providerField('country.id'), true),
      },
    },
    {
      id: 'region',
      subgroup: MrtColumnSubgroup.CompanyDetails,
      headerIntlId: 'schemas.supplier.region',
      includes: [providerField('country.region')],
      isHiddenByDefault: true,
      enableSorting: true,
      sortField: providerField('country.region.name'),
      renderCell: renderProviderCell(provider =>
        provider.country?.region?.name ? (
          <ValueCell value={provider.country.region.name} maxRows={1} disableCell />
        ) : (
          <EmptyCell disableCell />
        ),
      ),
      filter: multiSelectFilter('country.region.id', 'country.region.name', true),
    },
    {
      id: 'contacts',
      subgroup: MrtColumnSubgroup.StatusAndAdministration,
      headerIntlId: 'schemas.provider.contacts',
      includes: [
        providerField('organization.contacts.user'),
        providerField('referralContact'),
        providerField('referralContact.referralEmailHistory'),
      ],
      renderCell: renderProviderCell(provider => {
        const isConnected = provider.linkStatus === ProviderLinkStatus.Connected
        const hasContact = isConnected
          ? Boolean(provider.organization?.contacts?.length)
          : Boolean(provider.referralContact?.name)
        if (!hasContact) return <EmptyCell disableCell />
        return isConnected ? (
          <UserGroupCell
            users={provider.organization?.contacts?.map(contact => contact.user)}
            subTitle={provider.name}
            label={formatMessage({ id: 'general.contacts' }, { count: provider.organization?.contacts?.length })}
            title={formatMessage({ id: 'schemas.provider.contacts' })}
            disableCell
          />
        ) : (
          <ReferralContactCell provider={provider} drawer disableCell />
        )
      }),
      // 'contacts' is a backend custom filter that spans referral + organization contacts for
      // empty/notEmpty; is/isNot match organization contacts via the facet options below.
      filter: multiSelectFilter('contacts', 'organization.contacts.user.name', false),
    },
    {
      id: 'ownerUsers',
      subgroup: MrtColumnSubgroup.StatusAndAdministration,
      headerIntlId: 'schemas.provider.ownerUsers',
      includes: [providerField('providerOwners.user')],
      renderCell: renderProviderCell(provider =>
        provider.ownerUsers?.length ? (
          <UserGroupCell
            users={provider.ownerUsers}
            subTitle={provider.name}
            label={formatMessage({ id: 'general.owners' }, { count: provider.ownerUsers?.length })}
            title={formatMessage({ id: 'schemas.provider.ownerUsers' })}
            disableCell
          />
        ) : (
          <EmptyCell disableCell />
        ),
      ),
      filter: {
        field: providerField('providerOwners.userId'),
        variant: 'multi-select',
        facet: { name: providerField('providerOwners.user.name') },
        toFilterGroups: multiSelectToIn(providerField('providerOwners.userId'), false),
      },
    },
    {
      id: 'websiteAddress',
      subgroup: MrtColumnSubgroup.Identity,
      headerIntlId: 'schemas.supplier.websiteAddress',
      includes: [providerField('organization'), providerField('enrichments')],
      size: 280,
      enableSorting: true,
      sortField: providerField('websiteAddress'),
      renderCell: renderProviderCell(provider => (
        <EnrichedProviderValueCell provider={provider} field="websiteAddress" isLink />
      )),
      filter: multiSelectFilter('websiteAddress', 'websiteAddress', false, true),
    },
    {
      id: 'spend',
      subgroup: MrtColumnSubgroup.StatusAndAdministration,
      headerIntlId: 'schemas.supplier.annualSpend.spend',
      includes: [providerField('spends')],
      enableSorting: true,
      sortField: providerField('spends.amount'),
      renderCell: renderSpendCell(spendPeriodFocus),
      withFilterValue: filterValue => ({
        ...spendHeader(filterValue, spendPeriodFocus, formatMessage),
        renderCell: renderSpendCell(spendDisplayPeriod(filterValue, spendPeriodFocus)),
      }),
      filter: spendColumnFilter({
        field: providerField('spends.amount'),
        options: spendFilterOptions,
        formatMessage,
        currencyUnit: organizationCurrency,
        // The period only became part of the selection here; a value stored before that was implicitly
        // about the organization's focused period, and still is.
        toFilterGroups: createSpendToFilterGroups(spendFilterFields(providerField('spends')), {
          defaultPeriodName: spendPeriodFocus,
        }),
        countTarget: spendCountTarget,
      }),
    },
    {
      id: 'industry',
      subgroup: MrtColumnSubgroup.CompanyDetails,
      headerIntlId: 'schemas.organization.industryNace1',
      includes: [providerField('organization'), providerField('enrichments'), providerField('industry')],
      enableSorting: true,
      sortField: providerField('industry.description'),
      renderCell: renderProviderCell(provider => <EnrichedProviderIndustryCell provider={provider} />),
      filter: {
        field: providerField('industry.id'),
        variant: 'multi-select',
        facet: { name: providerField('industry.description') },
        toFilterGroups: multiSelectToIn(providerField('industry.id'), true),
      },
    },
    {
      id: 'subIndustry',
      subgroup: MrtColumnSubgroup.CompanyDetails,
      headerIntlId: 'schemas.organization.subIndustryNace2',
      includes: [providerField('organization'), providerField('enrichments'), providerField('subIndustry')],
      isHiddenByDefault: true,
      enableSorting: true,
      sortField: providerField('subIndustry.description'),
      renderCell: renderProviderCell(provider => <EnrichedProviderSubIndustryCell provider={provider} />),
      filter: multiSelectFilter('subIndustry.id', 'subIndustry.description', true),
    },
    {
      id: 'registrationNumber',
      subgroup: MrtColumnSubgroup.Identity,
      headerIntlId: 'schemas.supplier.registrationNumber',
      includes: [providerField('organization'), providerField('enrichments')],
      enableSorting: true,
      sortField: providerField('registrationNumber'),
      renderCell: renderProviderCell(provider => (
        <EnrichedProviderValueCell provider={provider} field="registrationNumber" />
      )),
      filter: multiSelectFilter('registrationNumber', 'registrationNumber', false, true),
    },
    {
      id: 'vatNumber',
      subgroup: MrtColumnSubgroup.Identity,
      headerIntlId: 'schemas.supplier.vatNumber',
      includes: [providerField('organization'), providerField('enrichments')],
      enableSorting: true,
      sortField: providerField('vatNumber'),
      renderCell: renderProviderCell(provider => <EnrichedProviderValueCell provider={provider} field="vatNumber" />),
      filter: multiSelectFilter('vatNumber', 'vatNumber', false, true),
    },
    {
      id: 'revenue',
      subgroup: MrtColumnSubgroup.CompanyDetails,
      headerIntlId: 'schemas.organization.revenue',
      includes: [providerField('organization'), providerField('enrichments')],
      renderCell: renderProviderCell(provider => (
        <EnrichedProviderValueCell
          provider={provider}
          field="revenueRange"
          formatValue={value => formatMessage({ id: `claim.companyRevenue.${value}` })}
        />
      )),
      filter: enumMultiSelectFilter('revenueRange', 'claim.companyRevenue'),
    },
    {
      id: 'employees',
      subgroup: MrtColumnSubgroup.CompanyDetails,
      headerIntlId: 'schemas.organization.employees',
      includes: [providerField('organization'), providerField('enrichments')],
      renderCell: renderProviderCell(provider => (
        <EnrichedProviderValueCell
          provider={provider}
          field="companySizeRange"
          formatValue={value => formatMessage({ id: `claim.companySize.${value}` })}
        />
      )),
      filter: enumMultiSelectFilter('companySizeRange', 'claim.companySize'),
    },
    ...insertIf<MrtColumnConfig<T>>(canSeeNonFinanceColumns, {
      id: 'activityStatus',
      subgroup: MrtColumnSubgroup.StandardCategories,
      headerIntlId: 'schemas.provider.activityStatus',
      includes: [],
      enableSorting: true,
      sortField: providerField('activityStatus'),
      renderCell: renderProviderCell(provider => (
        <EnumCategoryCell
          value={provider.activityStatus}
          valuesIntlPrefix="schemas.provider.activityStatusValues"
          disableCell
        />
      )),
      filter: enumMultiSelectFilter('activityStatus', 'schemas.provider.activityStatusValues', ProviderActivityStatus),
    }),
    {
      id: 'finalRiskRating',
      subgroup: MrtColumnSubgroup.StandardCategories,
      headerIntlId: 'schemas.provider.finalRiskRating',
      includes: [],
      enableSorting: true,
      sortField: providerField('finalRiskRating'),
      renderCell: renderProviderCell(provider =>
        !provider.finalRiskRating || isEmptyEquivalentValue(provider.finalRiskRating) ? (
          <EmptyCell disableCell />
        ) : (
          <FinalRiskRatingCell value={provider.finalRiskRating} maxRows={1} disableCell />
        ),
      ),
      filter: {
        field: providerField('finalRiskRating'),
        variant: 'multi-select',
        facet: {
          name: providerField('finalRiskRating'),
          isEnum: true,
          matchInSearch: true,
          formatLabel: formatEnumFacetLabel('schemas.provider.finalRiskRatingValues', formatMessage),
        },
        toFilterGroups: enumToFilterGroups(providerField('finalRiskRating'), FinalRiskRating),
      },
    },
    {
      id: 'priority',
      subgroup: MrtColumnSubgroup.StandardCategories,
      headerIntlId: 'schemas.provider.priority',
      includes: [],
      enableSorting: true,
      sortField: providerField('priority'),
      renderCell: renderProviderCell(provider => (
        <EnumCategoryCell value={provider.priority} valuesIntlPrefix="schemas.provider.priorityValues" disableCell />
      )),
      filter: enumMultiSelectFilter('priority', 'schemas.provider.priorityValues', ProviderPriority),
    },
    ...insertIf<MrtColumnConfig<T>>(canSeeNonFinanceColumns, {
      id: 'providerApprovalStatus',
      subgroup: MrtColumnSubgroup.StandardCategories,
      headerIntlId: 'schemas.provider.providerApprovalStatus',
      includes: [],
      enableSorting: true,
      sortField: providerField('providerApprovalStatus'),
      renderCell: renderProviderCell(provider => (
        <EnumCategoryCell
          value={provider.providerApprovalStatus}
          valuesIntlPrefix="schemas.provider.providerApprovalStatusValues"
          disableCell
        />
      )),
      filter: enumMultiSelectFilter(
        'providerApprovalStatus',
        'schemas.provider.providerApprovalStatusValues',
        ProviderApprovalStatus,
      ),
    }),
    ...insertIf<MrtColumnConfig<T>>(isSourcingUser, {
      id: 'tier',
      subgroup: MrtColumnSubgroup.StandardCategories,
      headerIntlId: 'schemas.provider.tier',
      includes: [],
      enableSorting: true,
      sortField: providerField('tier'),
      renderCell: renderProviderCell(provider =>
        provider.tier ? (
          <CategoryCell
            value={formatMessage({ id: 'schemas.provider.tierValues' }, { tier: provider.tier })}
            disableCell
          />
        ) : (
          <EmptyCell disableCell />
        ),
      ),
      filter: {
        field: providerField('tier'),
        variant: 'multi-select',
        facet: {
          name: providerField('tier'),
          isEnum: true,
          formatLabel: formatTierLabel(formatMessage),
        },
        toFilterGroups: multiSelectToIn(providerField('tier'), true),
      },
    }),
    ...insertIf<MrtColumnConfig<T>>(isSourcingUser, {
      id: 'supplierUsage',
      subgroup: MrtColumnSubgroup.StandardCategories,
      headerIntlId: 'schemas.provider.supplierUsage',
      includes: [],
      enableSorting: true,
      sortField: providerField('supplierUsage'),
      renderCell: renderProviderCell(provider => (
        <EnumCategoryCell
          value={provider.supplierUsage}
          valuesIntlPrefix="schemas.provider.supplierUsageValues"
          disableCell
        />
      )),
      filter: enumMultiSelectFilter('supplierUsage', 'schemas.provider.supplierUsageValues', SupplierUsage),
    }),
    {
      id: 'customId',
      subgroup: MrtColumnSubgroup.Identity,
      headerIntlId: 'schemas.organization.customId',
      includes: [],
      enableSorting: true,
      sortField: providerField('customId'),
      renderCell: renderProviderCell(provider =>
        provider.customId ? <ValueCell value={provider.customId} maxRows={1} disableCell /> : <EmptyCell disableCell />,
      ),
      filter: multiSelectFilter('customId', 'customId', false, true),
    },
    ...categoryColumns,
    {
      id: 'createdAt',
      subgroup: MrtColumnSubgroup.StatusAndAdministration,
      headerIntlId: 'schemas.supplier.created',
      includes: [],
      enableSorting: true,
      sortField: providerField('createdAt'),
      renderCell: renderProviderCell(provider =>
        provider.createdAt ? <DateCell value={provider.createdAt} disableCell /> : <EmptyCell disableCell />,
      ),
      filter: {
        field: providerField('createdAt'),
        variant: 'date-range',
        toFilterGroups: dateRangeToFilterGroups(providerField('createdAt')),
      },
    },
  ]

  return columns.map(column => {
    const providerColumn = { ...column, group: MrtColumnGroup.Provider }
    return providerPath ? asFilterOnlyColumn({ ...providerColumn, id: prefixProviderId(column.id) }) : providerColumn
  })
}
